import React, { useCallback, useMemo, useState } from 'react';
import { Linking, Text as RNText, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import FastImage from 'utils/packages/rnFastImage';
import { formatDistanceToNow } from 'date-fns';
import { CardView, CARD_VIEW_BORDER_RADIUS, rem, Spacer } from 'design-system';
import { IconEmma } from 'design-system/icons';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import links from 'utils/links';
import createStyleSheets from 'utils/createStyleSheets';
import { USE_SAFARI_VIEW_OR_CHROME_TABS_FOR_NEWS } from '../constants';
import { isWeb } from '../../../constants';
const IMAGE_SIZE = 64;
const IMAGE_BORDER_RADIUS = 12;
const CARD_INTERNAL_PADDING = 16;
const CARD_CONTENT_VISIBILITY_OFFSET = 24;
const LEFT_SPACING = 16;
const SPACING_BETWEEN_ITEMS = 8;
const MAX_WIDTH = 328;
const getCardWidth = (width) => width - rem(LEFT_SPACING + SPACING_BETWEEN_ITEMS + CARD_INTERNAL_PADDING + CARD_CONTENT_VISIBILITY_OFFSET);
const NewsCard = ({ data, forList, noMarginRight }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { width } = useAppFrameDimensions(isWeb);
    const [showIconEmma, setShowIconEmma] = useState(true);
    const onPress = useCallback(() => {
        if (USE_SAFARI_VIEW_OR_CHROME_TABS_FOR_NEWS) {
            links.openLink(data.url);
        }
        else {
            Linking.openURL(data.url);
        }
    }, [data.url]);
    const content = useMemo(() => (<View style={styles.contentContainer}>
        <View style={styles.flex}>
          <Text Text-14 numberOfLines={3}>
            {data.headline}
          </Text>
          <Spacer.H8 />
          <Text Text-12 Gray>
            {`${data.source}`}
            <RNText style={styles.bullet} allowFontScaling={false}>
              {'   •   '}
            </RNText>
            <Text TextThin-12 Gray>{`${formatDistanceToNow(new Date(data.mTimestamp * 60000), {
            addSuffix: true,
        })}`}</Text>
          </Text>
        </View>
        {forList ? <Spacer.W16 /> : <Spacer.W8 />}
        <View style={styles.imageContainer}>
          {showIconEmma ? <IconEmma scale={2} color={colors.elements.secondaryLight}/> : null}
          <FastImage style={styles.image} source={{ uri: data.image }} onLoad={() => {
            setShowIconEmma(false);
        }}/>
        </View>
      </View>), [data.headline, data.source, data.mTimestamp, data.image, forList, showIconEmma, colors, styles]);
    if (forList) {
        return (<TouchableBounce onPress={onPress}>
        <CardView noMarginBottom>
          <View style={styles.containerForList}>{content}</View>
        </CardView>
      </TouchableBounce>);
    }
    const cardWidth = Math.min(rem(MAX_WIDTH), getCardWidth(width));
    return (<TouchableBounce onPress={onPress} style={[styles.container, styles.touchable, noMarginRight && styles.noMarginRight, { width: cardWidth }]}>
      <CardView noMarginTop noMarginBottom style={[styles.container, { width: cardWidth }]}>
        {content}
      </CardView>
    </TouchableBounce>);
};
const styleSet = createStyleSheets((colors) => ({
    touchable: {
        marginRight: rem(SPACING_BETWEEN_ITEMS),
        padding: 0,
    },
    noMarginRight: {
        marginRight: 0,
    },
    container: {
        margin: 0,
        padding: rem(CARD_INTERNAL_PADDING),
        flexDirection: 'row',
        overflow: 'visible',
        borderRadius: CARD_VIEW_BORDER_RADIUS,
    },
    containerForList: {
        margin: 0,
        padding: rem(CARD_INTERNAL_PADDING),
        flexDirection: 'row',
    },
    contentContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    imageContainer: {
        width: rem(IMAGE_SIZE),
        height: rem(IMAGE_SIZE),
        borderRadius: rem(IMAGE_BORDER_RADIUS),
        backgroundColor: colors.cards.transparentOnDark,
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        position: 'absolute',
        width: rem(IMAGE_SIZE),
        height: rem(IMAGE_SIZE),
        borderRadius: rem(IMAGE_BORDER_RADIUS),
    },
    bullet: {
        fontWeight: '900',
    },
}));
export default NewsCard;
